import axios from "axios";
import React, { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Grid2,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
      background: "#EBF3FD",
    },
  },
};

const Step4 = ({
  sra,
  setSra,
  setStep,
  approvers,
  setApprovers,
  noOfApprovers,
  setNoOfApprovers,
  storedUser,
  approvalLimit,
  maxApprovalLimit,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const amount = Number(sra?.amount ?? 0);
  const limit = Number(approvalLimit ?? 0);
  const maxLimit = Number(maxApprovalLimit ?? 0);
  const [isSOApproved, setIsSOApproved] = useState(false);

  useEffect(() => {
    const getSOStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/draft-invoice-approval/get-amounts?soId=${sra.po}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setIsSOApproved(res.data?.isSOApproved);
      } catch (error) {
        console.log(error);
      }
    };
    getSOStatus();
  }, []);

  const requiresCMDTier =
    !isSOApproved && maxLimit > 0 && limit > 0 && amount >= maxLimit;
  const requiresCFOTier =
    !isSOApproved &&
    !requiresCMDTier &&
    limit > 0 &&
    amount >= limit &&
    (maxLimit === 0 || amount < maxLimit);

  const cfoUsers = orgUsers.filter(
    (u) => u.userRoleName === "CFO" && u.isEnable,
  );
  const cmdUsers = orgUsers.filter(
    (u) => u.userRoleName === "CMD" && u.isEnable,
  );

  const buildEditApproversArray = (current, users, count) => {
    const cfos = users.filter((u) => u.userRoleName === "CFO" && u.isEnable);
    const cmds = users.filter((u) => u.userRoleName === "CMD" && u.isEnable);

    const resized = Array.from({ length: count }, (_, i) => current[i] || "");

    if (requiresCMDTier && count >= 2) {
      if (!resized[count - 1] && cmds.length === 1)
        resized[count - 1] = cmds[0].email;
      if (!resized[count - 2] && cfos.length === 1)
        resized[count - 2] = cfos[0].email;
    } else if (requiresCFOTier && count >= 1) {
      if (!resized[count - 1] && cfos.length === 1)
        resized[count - 1] = cfos[0].email;
    }

    return resized;
  };

  useEffect(() => {
    const getOrgUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setOrgUsers(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getOrgUsers();
  }, []);

  useEffect(() => {
    setApprovers((prev) =>
      buildEditApproversArray(prev, orgUsers, noOfApprovers),
    );
  }, [noOfApprovers]);

  useEffect(() => {
    if (noOfApprovers === 0) return;
    setApprovers((prev) =>
      buildEditApproversArray(prev, orgUsers, noOfApprovers),
    );
  }, [orgUsers]);

  useEffect(() => {
    const responseApprovers = sra?.approvers
      ?.sort((a, b) => a.number - b.number)
      ?.map((item) => item.approver?.email || "");
    setApprovers(responseApprovers);
  }, []);

  const getSlotOptions = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot) {
        return cmdUsers.filter(
          (u) =>
            !approvers.some((email, i) => i !== index && email === u.email),
        );
      }
      if (isSecondLastSlot) {
        return cfoUsers.filter(
          (u) =>
            !approvers.some((email, i) => i !== index && email === u.email),
        );
      }
    }

    if (requiresCFOTier && isLastSlot) {
      return cfoUsers.filter(
        (u) => !approvers.some((email, i) => i !== index && email === u.email),
      );
    }

    const reservedRoles = [
      ...(requiresCMDTier ? ["CFO", "CMD"] : []),
      ...(requiresCFOTier ? ["CFO"] : []),
    ];
    const excludedRoles = [
      "BUYER",
      "FINANCE_PROCESSOR",
      "SUPERUSER",
      "ACCOUNT_PAYABLE_TEAM",
      ...reservedRoles,
    ];

    return orgUsers.filter(
      (user) =>
        !excludedRoles.includes(user.userRoleName) &&
        !approvers.some((email, i) => i !== index && email === user.email) &&
        user.isEnable,
    );
  };

  const getSlotLabel = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot)
        return (
          <span style={{ color: "#EA981F", marginLeft: 4 }}>
            (CMD required)
          </span>
        );
      if (isSecondLastSlot)
        return (
          <span style={{ color: "#EA981F", marginLeft: 4 }}>
            (CFO required)
          </span>
        );
    }
    if (requiresCFOTier && isLastSlot) {
      return (
        <span style={{ color: "#EA981F", marginLeft: 4 }}>(CFO required)</span>
      );
    }
    return null;
  };

  const getPlaceholder = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot) return "Select CMD";
      if (isSecondLastSlot) return "Select CFO";
    }
    if (requiresCFOTier && isLastSlot) return "Select CFO";
    return "Select Approver";
  };

  const isSlotLocked = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot && cmdUsers.length === 1) return true;
      if (isSecondLastSlot && cfoUsers.length === 1) return true;
    }
    if (requiresCFOTier && isLastSlot && cfoUsers.length === 1) return true;

    return false;
  };

  const handleApproversChange = (index, value) => {
    const updated = [...approvers];
    updated[index] = value;
    setApprovers(updated);
  };

  const validateForm = () => {
    const newErrors = {};

    if (requiresCMDTier && noOfApprovers < 2) {
      newErrors.noOfApprovers =
        "This amount requires at least 2 approvers (CFO + CMD).";
    }

    approvers.slice(0, noOfApprovers).forEach((approver) => {
      if (!approver || approver.trim?.() === "") {
        newErrors.approver =
          "Please select all approvers as per the selected number.";
      }
    });

    if (!newErrors.approver) {
      if (requiresCMDTier && noOfApprovers >= 2) {
        const lastUser = orgUsers.find(
          (u) => u.email === approvers[noOfApprovers - 1],
        );
        const secondLastUser = orgUsers.find(
          (u) => u.email === approvers[noOfApprovers - 2],
        );
        if (!lastUser || lastUser.userRoleName !== "CMD") {
          newErrors.approver =
            "The last approver must be a CMD for this amount.";
        } else if (!secondLastUser || secondLastUser.userRoleName !== "CFO") {
          newErrors.approver =
            "The second-last approver must be a CFO for this amount.";
        }
      } else if (requiresCFOTier && noOfApprovers > 0) {
        const lastUser = orgUsers.find(
          (u) => u.email === approvers[noOfApprovers - 1],
        );
        if (!lastUser || lastUser.userRoleName !== "CFO") {
          newErrors.approver =
            "The last approver must be a CFO for this amount.";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => setStep((prev) => prev - 1);

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Approval Authority Selection</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "80%" }}></span>
      </div>

      <div className="dashboard-container">
        <Typography className="form-title">Select Approvers</Typography>
        <label style={{ fontWeight: "bold" }}>Number of Approvers:</label>
        <select
          className={`${errors.noOfApprovers && "input-error"}`}
          value={noOfApprovers}
          onChange={(e) => {
            setNoOfApprovers(Number(e.target.value));
            setErrors({ ...errors, noOfApprovers: "" });
          }}
          style={{ marginLeft: "10px", padding: "5px", width: "200px" }}
        >
          <option value="">Select</option>
          {[1, 2, 3, 4]
            .filter((n) => !requiresCMDTier || n >= 2)
            .map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
        </select>
        {errors.noOfApprovers && (
          <p className="error">{errors.noOfApprovers}</p>
        )}
        <Grid2 container spacing={2}>
          {Array.from({ length: noOfApprovers }, (_, index) => {
            const locked = isSlotLocked(index);
            const slotOptions = getSlotOptions(index);

            return (
              <Grid2 key={index} size={{ xs: 12, sm: 4 }}>
                <label>
                  Approver {index + 1}
                  {getSlotLabel(index)}:
                </label>
                <Select
                  value={approvers[index] || ""}
                  onChange={(e) => handleApproversChange(index, e.target.value)}
                  displayEmpty
                  disabled={locked}
                  IconComponent={(props) => (
                    <KeyboardArrowDownIcon {...props} fontSize="small" />
                  )}
                  MenuProps={MenuProps}
                  sx={{
                    display: "flex",
                    borderRadius: "7px",
                    padding: "8px 7px",
                    fontSize: "14px",
                    margin: "7px 0 20px 0",
                    opacity: locked ? 0.7 : 1,
                    "& .MuiSelect-select": {
                      fontFamily: "Inter !important",
                      padding: "5px",
                      "&:hover": { cursor: locked ? "not-allowed" : "default" },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {getPlaceholder(index)}
                  </MenuItem>
                  {slotOptions.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      {user.name} ({user.userRoleName.replace(/_/g, " ")})
                    </MenuItem>
                  ))}
                </Select>
              </Grid2>
            );
          })}
        </Grid2>
        {errors.approver && <p className="error">{errors.approver}</p>}
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={handleNext}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step4;
